'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import { useMediaQuery } from 'usehooks-ts';
import MyWorldsSkeleton from '@/app/[locale]/(worlds)/profile/components/MyWorldsSkeleton';
import LoadMoreButton from '@/components/LoadMoreButton';
import ViverseWorldCard from '@/components/ViverseWorldCard';
import { useAppLoaderContext } from '@/context/AppLoaderContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useDigitalAssetPods } from '@/hooks/useDigitalAssetPods';
import { useDraggingScroll } from '@/hooks/useDraggingScroll';
import useHash from '@/hooks/useHash';
import { useRoomPolicy } from '@/hooks/useRoomPolicy';
import { useScrollable } from '@/hooks/useScrollable';
import { cn } from '@/lib/utils';
import { getCardType } from '@/lib/viverse-world-card';
import { getMyPersonas, RoomsResults } from '@/services/cms';
const LIMIT = 8;
const getKey = (pageIndex: number, previousPageData: RoomsResults) => {
  if (pageIndex === 0 || !previousPageData) return `myPersonas?start=0`;

  // reached the end
  if (!previousPageData.results) return null;

  // "/api/hubs-cms/v2/my-rooms?limit=8&start=16"
  const next = previousPageData.pages.next;
  const query = next.split('?')[1];
  const start = new URLSearchParams(query).get('start');
  return `myPersonas?start=${start}`;
};
function UserMyPersonas() {
  const $ = _c(61);
  const t = useTranslations();
  const hash = useHash();
  const roomPolicy = useRoomPolicy();
  const {
    store
  } = useConfigsContext();
  const {
    setIsAppLoading
  } = useAppLoaderContext();
  const {
    refreshDigitalAssetPods
  } = useDigitalAssetPods();
  const htcToken = store?.credentials?.htcToken;
  const stopScroll = useMediaQuery("(min-width: 1440px)");
  const {
    containerRef,
    canScrollLeft,
    canScrollRight
  } = useScrollable(300, stopScroll);
  const {
    bind,
    disableDrag,
    onMouseDown,
    onMouseUp
  } = useDraggingScroll(containerRef);
  let t0;
  if ($[0] !== htcToken) {
    t0 = key => {
      const query = key.split("?")[1];
      const start = Number(query.split("=")[1]);
      return getMyPersonas(htcToken, start, LIMIT).then(_temp);
    };
    $[0] = htcToken;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      revalidateFirstPage: false
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data,
    isLoading,
    isValidating,
    mutate,
    size,
    setSize
  } = useSWRInfinite(getKey, t0, t1);
  let t2;
  if ($[3] !== mutate || $[4] !== refreshDigitalAssetPods || $[5] !== setIsAppLoading) {
    t2 = function handleAfterMutatingWorld() {
      refreshDigitalAssetPods?.();
      mutate().then(() => setIsAppLoading(false));
    };
    $[3] = mutate;
    $[4] = refreshDigitalAssetPods;
    $[5] = setIsAppLoading;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleAfterMutatingWorld = t2;
  let t3;
  if ($[7] !== data) {
    const rooms = [...(data?.flatMap(_temp2) || [])];
    const sortedRooms = rooms.sort(_temp3);
    t3 = sortedRooms.filter(_temp4).filter(_temp5);
    $[7] = data;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const uniqueRooms = t3;
  const allRooms = uniqueRooms;
  let t4;
  if ($[9] !== allRooms) {
    t4 = allRooms.filter(_temp6);
    $[9] = allRooms;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const stopSetAsFeatured = t4.length >= 3;
  let t5;
  if ($[11] !== hash) {
    t5 = () => {
      if (hash === "#persona") {
        const element = document.getElementById("persona");
        if (element) {
          element.scrollIntoView({
            behavior: "smooth"
          });
        }
      }
    };
    $[11] = hash;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== allRooms.length || $[14] !== hash || $[15] !== isLoading) {
    t6 = [hash, isLoading, allRooms.length];
    $[13] = allRooms.length;
    $[14] = hash;
    $[15] = isLoading;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  useEffect(t5, t6);
  const total = data?.[0]?.pages?.total || 0;
  const enableLoadMore = total > allRooms.length;
  if (allRooms.length === 0) {
    return null;
  }
  let t7;
  if ($[17] !== t) {
    t7 = t("profile-page.anchors.persona");
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <h2 className="subheader-1 md:header-4 relative mb-4 font-bold md:mb-6">{t7}</h2>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== bind) {
    t9 = bind();
    $[21] = bind;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = !disableDrag && (canScrollLeft || canScrollRight);
  const t11 = !disableDrag && (canScrollLeft || canScrollRight);
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = cn("no-scrollbar flex w-full flex-col max-lg:overflow-scroll max-lg:scroll-smooth", {
      "active:cursor-grabbing": t10,
      "cursor-grab": t11
    });
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== allRooms || $[27] !== handleAfterMutatingWorld || $[28] !== roomPolicy || $[29] !== stopSetAsFeatured) {
    let t14;
    if ($[31] !== handleAfterMutatingWorld || $[32] !== roomPolicy || $[33] !== stopSetAsFeatured) {
      t14 = room_2 => <div key={`my-rooms-${room_2.id}`}><ViverseWorldCard className="max-w-[306px] min-w-[306px] lg:max-w-[322px] lg:min-w-[322px]" info={room_2} showMoreAction={false} onAfterMutatingWorld={handleAfterMutatingWorld} customDesc={Object.values(roomPolicy).find(policy => policy.value === room_2.policy)?.title || room_2.policy} stopSetAsFeatured={stopSetAsFeatured} hidePremiumBadge={false} cardType={room_2.content_type ? getCardType(room_2.content_type) : "experiences"} /></div>;
      $[31] = handleAfterMutatingWorld;
      $[32] = roomPolicy;
      $[33] = stopSetAsFeatured;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    t13 = allRooms.map(t14);
    $[26] = allRooms;
    $[27] = handleAfterMutatingWorld;
    $[28] = roomPolicy;
    $[29] = stopSetAsFeatured;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[35] !== allRooms.length || $[36] !== isLoading || $[37] !== isValidating) {
    t14 = (isLoading || isValidating) && <MyWorldsSkeleton count={allRooms.length} className="max-w-[306px] min-w-[306px] lg:max-w-[322px] lg:min-w-[322px]" />;
    $[35] = allRooms.length;
    $[36] = isLoading;
    $[37] = isValidating;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t13 || $[40] !== t14) {
    t15 = <div className="flex gap-5 md:-mx-2 md:gap-2 lg:flex-wrap lg:gap-2">{t13}{t14}</div>;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== containerRef || $[43] !== onMouseDown || $[44] !== onMouseUp || $[45] !== t12 || $[46] !== t15 || $[47] !== t9) {
    t16 = <div {...t9} ref={containerRef} className={t12} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>{t15}</div>;
    $[42] = containerRef;
    $[43] = onMouseDown;
    $[44] = onMouseUp;
    $[45] = t12;
    $[46] = t15;
    $[47] = t9;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== enableLoadMore || $[50] !== isLoading || $[51] !== isValidating || $[52] !== setSize || $[53] !== size) {
    t17 = enableLoadMore && <div className="flex flex-1 justify-center"><LoadMoreButton isLoading={isLoading || isValidating} onClick={() => setSize(size + 1)} /></div>;
    $[49] = enableLoadMore;
    $[50] = isLoading;
    $[51] = isValidating;
    $[52] = setSize;
    $[53] = size;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== t16 || $[56] !== t17) {
    t18 = <div className="flex flex-col gap-10">{t16}{t17}</div>;
    $[55] = t16;
    $[56] = t17;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== t18 || $[59] !== t8) {
    t19 = <section id="persona" className="flex scroll-mt-40 flex-col px-4 md:px-8 lg:px-18">{t8}{t18}</section>;
    $[58] = t18;
    $[59] = t8;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  return t19;
}
function _temp6(room_1) {
  return room_1?.is_featured;
}
function _temp5(room_0) {
  return room_0 !== undefined;
}
function _temp4(room, index, self) {
  return index === self.findIndex(r => r?.id === room?.id);
}
function _temp3(a, b) {
  return Number(b?.is_standalone) - Number(a?.is_standalone);
}
function _temp2(page) {
  return page?.results;
}
function _temp(res) {
  return res?.results;
}
export default UserMyPersonas;